import { Button } from '@/components/Atoms/Button/Button';
import BellActive from '@/components/Icons/BellActive';
import BellInactive from '@/components/Icons/BellInactive';
import { CsrfForm } from '@/components/Molecules/CsrfForm/CsrfForm';
import { Dialog, DialogActions, DialogActionsMain, DialogActionsSecondary, useDialog } from '@/components/Molecules/Dialog/Dialog';
import { useMounted } from '@/hooks/useMounted';
import useTranslation from '@/hooks/useTranslation';
import type { ISubscriptionState } from '@/interfaces';
import { trackEvent } from '@/services/Analytics/BBAnalytics';
import { TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import { useState } from 'react';
interface IProps {
  subscriptionState?: ISubscriptionState;
  eventId: string;
}

/**
 * A button that maintains the subscription state of an item.
 */
export const RegisterButton = ({
  subscriptionState,
  eventId
}: IProps) => {
  const {
    t
  } = useTranslation('common');
  const isMounted = useMounted();
  const [internalSubscriptionState, setInternalSubscriptionState] = useState(subscriptionState);
  const [isLoading, setLoading] = useState(false);
  const successDialog = useDialog({
    lazy: true
  });
  const errorDialog = useDialog({
    lazy: true
  });
  if (!subscriptionState) {
    return null;
  }
  const onSubmitStart = () => {
    setLoading(true);
  };
  const onSubmitEnd = (response: any) => {
    if (isMounted()) {
      setInternalSubscriptionState(response.subscriptionState);
      setLoading(false);
    }
    // Attempt to mutate source object, so the change persists even if the button is un-mounted / re-mounted
    subscriptionState.isSubscribed = response.subscriptionState.isSubscribed;
    subscriptionState.nextAction = response.subscriptionState.nextAction;
    if (response.subscriptionState.isSubscribed && isMounted()) {
      successDialog.open();
    }
  };
  const onSubmitError = () => {
    if (isMounted()) {
      errorDialog.open();
      setLoading(false);
    }
  };
  const icon = internalSubscriptionState.isSubscribed ? <BellActive /> : <BellInactive />;
  const text = t(internalSubscriptionState.isSubscribed ? 'Event.Registration.unregister' : 'Event.Registration.register');
  return <CsrfForm apiUrl={internalSubscriptionState.nextAction.url} values={{
    action: internalSubscriptionState.nextAction.action
  }} onSubmitStart={onSubmitStart} onSubmitEnd={onSubmitEnd} onSubmitError={onSubmitError}>
            <Button kind="tertiary" icon={icon} isLoading={isLoading} title={internalSubscriptionState.isSubscribed ? t('Event.Registration.tooltipUnregister') : t('Event.Registration.tooltipRegister')} type="submit" disabled={isLoading} onClick={() => trackEvent(internalSubscriptionState.isSubscribed ? TrackingAction.Unregister : TrackingAction.Register, TrackingCategory.Event, eventId)}>
                {text}
            </Button>
            <Dialog {...successDialog} title={t('Event.Registration.thankYouHeader')}>
                <p>{t('Event.Registration.thankYouBody')}</p>
                <DialogActions>
                    <DialogActionsMain>
                        <Button onClick={successDialog.close}>{t('Actions.close_modal')}</Button>
                    </DialogActionsMain>
                    <DialogActionsSecondary>
                        <a href={`/events/${eventId}/calendar`}>{t('Event.Registration.addToCalendar')}</a>
                    </DialogActionsSecondary>
                </DialogActions>
            </Dialog>
            <Dialog {...errorDialog} title={t('Event.Registration.errorHeader')}>
                <p>{t('Event.Registration.errorBody')}</p>
                <DialogActionsMain>
                    <Button onClick={errorDialog.close}>{t('Actions.close_modal')}</Button>
                </DialogActionsMain>
            </Dialog>
        </CsrfForm>;
};